import { Flex, Modal, Skeleton, Typography } from '@iheartjane/reefer';
import { Form, useFormContext } from '@iheartjane/reefer-hook-form';
import { useMemo } from 'react';
import { useLocation } from 'react-router';

import type { GlobalSpecialV2 } from '@jane/business-admin/types';
import {
  BODY_LINE_HEIGHT,
  FORM_FIELD_DEFAULT_MARGIN,
  MAX_MESSAGE_LENGTH,
  SPECIAL_TYPES,
} from '@jane/business-admin/util';

import {
  MAX_CUSTOM_BADGE_LENGTH,
  useTriggerFormValidation,
} from '../../shared/specials/modal/form';
import { DiscountTypeAndDiscountAmountField } from './DiscountTypeAndDiscountAmountField';
import { SpecialTypeCriteria } from './SpecialTypeCriteria';

const LoadingDetailsCard = () => {
  return (
    <Flex flexDirection="column">
      <Flex mb={40} width="100%">
        <Skeleton animate width="100%" key="loading_status">
          <Skeleton.Bone height={BODY_LINE_HEIGHT} mb={2} width="20%" />
          <Skeleton.Bone height={BODY_LINE_HEIGHT} width="20%" />
        </Skeleton>
      </Flex>
      <Flex mb={40} width="100%">
        <Skeleton animate width="100%" key="loading_name_input">
          <Skeleton.Bone height={BODY_LINE_HEIGHT} mb={12} width="20%" />
          <Skeleton.Bone height={48} width="100%" />
        </Skeleton>
      </Flex>
      <Flex mb={40} width="100%">
        <Skeleton animate width="100%" key="loading_special_type_select">
          <Skeleton.Bone height={BODY_LINE_HEIGHT} mb={2} width="30%" />
          <Skeleton.Bone height={BODY_LINE_HEIGHT} width="40%" />
        </Skeleton>
      </Flex>
      <Flex mb={40} width="100%">
        <Skeleton animate width="100%" key="loading_special_type_criteria">
          <Skeleton.Bone height={BODY_LINE_HEIGHT} mb={2} width="30%" />
          <Skeleton.Bone height={BODY_LINE_HEIGHT} width="40%" />
        </Skeleton>
      </Flex>
      <Flex mb={40} width="100%">
        {Array.from({ length: 2 }, (_, index) => (
          <Skeleton animate width="100%" key={`loading_2${index}`}>
            <Skeleton.Bone height={BODY_LINE_HEIGHT} mb={2} width="60%" />
            <Skeleton.Bone height={BODY_LINE_HEIGHT} width="80%" />
          </Skeleton>
        ))}
      </Flex>
      <Flex mb={20} width="100%">
        <Skeleton animate width="100%" key="loading_description_input">
          <Skeleton.Bone height={BODY_LINE_HEIGHT} mb={12} width="20%" />
          <Skeleton.Bone height={76} width="100%" />
        </Skeleton>
      </Flex>

      <Modal.ContentDivider />

      <Flex mt={20} mb={40} gap={24} width="100%">
        <Skeleton animate width="100%" key="loading_reservation_modes">
          <Skeleton.Bone height={BODY_LINE_HEIGHT} mb={12} width="20%" />
          <Skeleton.Bone height={BODY_LINE_HEIGHT} width="40%" />
        </Skeleton>
      </Flex>
      <Flex mb={40} width="100%">
        <Skeleton animate width="100%" key="loading_terms_input">
          <Skeleton.Bone height={BODY_LINE_HEIGHT} mb={12} width="20%" />
          <Skeleton.Bone height={76} width="100%" />
        </Skeleton>
      </Flex>
    </Flex>
  );
};

export const DetailsCard = ({
  special,
  isCreateMode,
  isEditable,
  isLoading,
}: {
  isCreateMode: boolean;
  isEditable?: boolean;
  isLoading: boolean;
  special?: GlobalSpecialV2 | null;
}) => {
  const { watch, trigger } = useFormContext();

  const { pathname } = useLocation();
  const pathIncludesDuplicate = pathname.includes('duplicate');

  const {
    custom_badge,
    description = '',
    special_type = '',
  } = {
    ...special,
  };

  // TODO: maybe update to useWatch()?
  const customBadgeWatch = watch('custom_badge');
  const descriptionWatch = watch('description');
  const specialTypeWatch = watch('special_type');
  const discountTypeWatch = watch('discount_type');

  const messageLength = useMemo(
    () =>
      descriptionWatch === ''
        ? 0
        : descriptionWatch?.length ||
          (!isCreateMode && description?.length) ||
          0,
    [descriptionWatch, description]
  );

  useTriggerFormValidation(
    ['description'],
    isLoading,
    messageLength > MAX_MESSAGE_LENGTH,
    trigger
  );

  const customBadgeLength = useMemo(
    () =>
      customBadgeWatch === ''
        ? 0
        : customBadgeWatch?.length ||
          (!isCreateMode && custom_badge?.length) ||
          0,
    [customBadgeWatch, custom_badge]
  );

  const specialTypeOptions = () => {
    const commonValues = [
      {
        value: SPECIAL_TYPES.BulkPricing,
        label: 'Bulk Pricing',
      },
      {
        value: SPECIAL_TYPES.Bundle,
        label: 'Buy X Get Y',
      },
      {
        value: SPECIAL_TYPES.Product,
        label: 'Product',
      },
      {
        value: SPECIAL_TYPES.CartTotal,
        label: 'Cart Total',
      },
      {
        value: SPECIAL_TYPES.QualifiedGroup,
        label: 'Qualified Group',
      },
      {
        value: SPECIAL_TYPES.SpendingThreshold,
        label: 'Spend X Get Y',
      },
    ];

    const selectValue = { value: 'select', label: 'Select' };

    if (isCreateMode) {
      return [selectValue].concat(commonValues);
    }

    return commonValues;
  };

  return (
    <>
      <Typography variant="h2-semibold" mb={FORM_FIELD_DEFAULT_MARGIN}>
        Details
      </Typography>
      {isLoading ? (
        <LoadingDetailsCard />
      ) : (
        <>
          <Typography color="grays-black" variant="body-semibold" mb={12}>
            Name
          </Typography>
          <Flex mb={FORM_FIELD_DEFAULT_MARGIN} gap={24}>
            <Form.TextField
              label="Name"
              name="name"
              required
              labelHidden
              maxLength={40}
              disabled={!isEditable}
            />
          </Flex>
          <Typography color="grays-black" variant="body-semibold" mb={12}>
            Special type
          </Typography>
          <Flex mb={FORM_FIELD_DEFAULT_MARGIN} gap={24}>
            {isCreateMode || pathIncludesDuplicate ? (
              <>
                <Form.SelectField
                  required
                  label="Special type"
                  labelHidden
                  name="special_type"
                  options={specialTypeOptions()}
                  width="100%"
                  validate={(value: string) => {
                    return value !== 'select'
                      ? true
                      : 'Must choose a valid special type';
                  }}
                />

                {/* Needed to get the spacing right */}
                <Flex width="100%">&nbsp;</Flex>
              </>
            ) : (
              <Flex width="50%" flexDirection="column">
                <Typography color="grays-black" as="p">
                  {
                    specialTypeOptions().find(
                      (type) =>
                        type.value === (specialTypeWatch || special_type)
                    )?.label
                  }
                </Typography>
              </Flex>
            )}
          </Flex>
          <Flex gap={24}>
            <SpecialTypeCriteria />
          </Flex>
          <Flex mb={FORM_FIELD_DEFAULT_MARGIN} gap={24}>
            <DiscountTypeAndDiscountAmountField />
          </Flex>
          {specialTypeWatch === SPECIAL_TYPES.Bundle &&
            discountTypeWatch === 'target_price' && (
              <Flex mb={FORM_FIELD_DEFAULT_MARGIN}>
                <Form.CheckboxField
                  name="display.bundle.settings.apply_target_price_per_unit"
                  label="Apply target price per-product"
                />
              </Flex>
            )}
          {specialTypeWatch === SPECIAL_TYPES.Product && (
            <Flex
              mb={FORM_FIELD_DEFAULT_MARGIN}
              width="100%"
              flexDirection="column"
            >
              <Flex gap={4}>
                <Typography color="grays-black" variant="body-semibold">
                  Custom badge title
                </Typography>
                <Typography color="grays-mid">
                  ({customBadgeLength}/{MAX_CUSTOM_BADGE_LENGTH} characters
                  used)
                </Typography>
              </Flex>
              <Form.TextField
                label="Custom badge title"
                labelHidden
                name="custom_badge"
                maxLength={MAX_CUSTOM_BADGE_LENGTH}
                data-testid="special-custom-badge"
              />
            </Flex>
          )}
          <Flex width="100%" flexDirection="column">
            <Flex gap={4}>
              <Typography color="grays-black" variant="body-semibold" mb={12}>
                Description
              </Typography>
              <Typography color="grays-mid">
                ({messageLength}/{MAX_MESSAGE_LENGTH} characters used)
              </Typography>
            </Flex>
            <Form.TextAreaField
              label="Description"
              labelHidden
              name="description"
              width="100%"
              maxLength={MAX_MESSAGE_LENGTH}
              rows={4}
              data-testid="special-description"
              disabled={!isEditable}
            />
          </Flex>

          <Modal.ContentDivider />

          <Flex
            width="100%"
            justifyContent="space-between"
            flexDirection="column"
          >
            <Typography color="grays-black" variant="body-semibold">
              Apply to reservation
            </Typography>
            <Flex mb={FORM_FIELD_DEFAULT_MARGIN}>
              <Flex gap={12} mt={12}>
                <Form.CheckboxField
                  name="reservation_modes.delivery"
                  onChange={() => null}
                  label="Delivery"
                  disabled={!isEditable}
                />
                <Form.CheckboxField
                  name="reservation_modes.pickup"
                  onChange={() => null}
                  label="Pickup"
                  disabled={!isEditable}
                />
              </Flex>
            </Flex>

            <Flex
              width="100%"
              justifyContent="space-between"
              flexDirection="column"
            >
              <Typography color="grays-black" variant="body-semibold">
                Can this special be used while other specials are present in the
                cart?
              </Typography>
              <Flex mt={12} flexDirection="row">
                <Form.RadioFieldGroup
                  row
                  name="stacking_setting"
                  disabled={!isEditable}
                  options={[
                    {
                      id: 'stacking-setting-yes',
                      label: 'Yes',
                      value: 'yes',
                    },
                    {
                      id: 'stacking-setting-no',
                      label: 'No',
                      value: 'no',
                    },
                    {
                      id: 'stacking-setting-combinable',
                      label: 'Only with other stackable specials',
                      value: 'combinable',
                    },
                  ]}
                />
              </Flex>
            </Flex>

            <Typography color="grays-black" variant="body-semibold" mb={12}>
              Terms and conditions
            </Typography>
            <Flex flexDirection="column" width="100%">
              <Form.TextAreaField
                label="Terms and conditions"
                labelHidden
                name="terms"
                width="100%"
                rows={4}
                disabled={!isEditable}
              />
            </Flex>
            <Flex mt={24} flexDirection="column">
              {specialTypeWatch !== SPECIAL_TYPES.QualifiedGroup && (
                <>
                  <Typography color="grays-black" variant="body-semibold">
                    Hide from Specials row and Specials tab?
                  </Typography>

                  <Flex mt={12} flexDirection="row">
                    <Form.RadioFieldGroup
                      row
                      name="hidden_from"
                      options={[
                        {
                          id: 'hidden-from-yes',
                          label: 'Yes',
                          value: true,
                        },
                        {
                          id: 'hidden-from-no',
                          label: 'No',
                          value: false,
                        },
                      ]}
                    />
                  </Flex>
                </>
              )}
            </Flex>
          </Flex>
        </>
      )}
    </>
  );
};
